import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconAlert, IconClock, IconPin } from './icons';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatRelative } from '../lib/format';

/**
 * Notfallmeldung, die sich in den Weg stellt (Kap. 13).
 *
 * Wird jemandem ein Notfall zugewiesen, genügt ein Punkt an der Glocke nicht:
 * Die Glocke sieht man, wenn man hinsieht, und bei einem Wasserrohrbruch ist
 * genau das die Frage. Deshalb legt sich die Meldung über die Oberfläche und
 * verlangt eine Bestätigung.
 *
 * Bewusst nur bei der Zuweisung eines Notfalls – nicht bei jeder Meldung der
 * Stufe `critical`. SLA-Überschreitungen tragen dieselbe Stufe, betreffen aber
 * Vorgänge, die die Person längst kennt; sie stehen in der Glocke und im
 * Lagebild. Würde jede davon so erscheinen, wäre der Griff zum Wegklicken
 * binnen zwei Tagen automatisiert – und der eine Notfall ginge unter wie zuvor.
 *
 * Die Bestätigung markiert die Benachrichtigung als gelesen. Damit erscheint
 * dieselbe Meldung nicht erneut, bleibt aber in der Glocke nachlesbar – wer
 * im Eifer wegklickt, findet sie wieder.
 */
export function EmergencyAlert() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  // Innerhalb einer Sitzung bereits gezeigte Meldungen: Zwischen Abfrage und
  // Bestätigung liegen Sekunden, in denen der Abrufer sie erneut liefern
  // würde – ohne diese Sperre blitzte die Meldung doppelt auf.
  const seen = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!user) return;
    let active = true;

    const load = async () => {
      try {
        const items = await api.get('/api/notifications/emergencies');
        if (!active) return;
        const fresh = (items ?? []).filter((item: any) => !seen.current.has(item.id));
        if (fresh.length === 0) return;
        for (const item of fresh) seen.current.add(item.id);
        setQueue((current) => [...current, ...fresh]);
      } catch {
        /* Ein Aussetzer der Abfrage darf die Oberfläche nicht stören. */
      }
    };

    void load();
    // Häufiger als die Glocke: Eine Minute Verzug ist bei einem Notfall zu
    // viel, und die Abfrage ist eine schmale Zeilenabfrage auf einem Index.
    const timer = setInterval(load, 20_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [user?.id]);

  const current = queue[0];
  if (!current) return null;

  const acknowledge = async (open: boolean) => {
    setBusy(true);
    try {
      await api.post(`/api/notifications/${current.id}/read`);
    } catch {
      /* Auch ohne Quittung weiterreichen – sie wiederholt sich beim nächsten Laden. */
    } finally {
      setBusy(false);
      setQueue((rest) => rest.slice(1));
      if (open && current.task_id) navigate(`/tasks/${current.task_id}`);
    }
  };

  const address = [current.street, [current.zip, current.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="emergency-backdrop" role="alertdialog" aria-modal="true" aria-labelledby="emergency-title">
      <div className="emergency-dialog">
        <div className="emergency-head">
          <span className="emergency-icon">
            <IconAlert width={22} height={22} />
          </span>
          <div>
            <span className="emergency-kicker">Notfall zugewiesen</span>
            <h2 id="emergency-title">{current.task_title ?? current.title}</h2>
            <span className="mono small">{current.task_no}</span>
          </div>
        </div>

        <div className="emergency-body">
          {current.property_name && (
            <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <IconPin width={15} height={15} />
              <span>
                <strong>{current.property_name}</strong>
                {address && <span className="small subtle" style={{ display: 'block' }}>{address}</span>}
              </span>
            </div>
          )}
          {current.due_at && (
            <div className="row" style={{ gap: 8 }}>
              <IconClock width={15} height={15} />
              <span>
                Frist: <strong>{formatDateTime(current.due_at)}</strong>{' '}
                <span className="small subtle">({formatRelative(current.due_at)})</span>
              </span>
            </div>
          )}
          <p className="small subtle" style={{ margin: 0 }}>
            {/* Ohne Punkt am Satzanfang: `formatRelative` liefert bereits „vor 1 Min.“ mit Abkürzungspunkt. */}
            Zugewiesen {formatRelative(current.created_at)} – sind Sie verhindert, melden Sie sich bitte sofort bei der
            Disposition.
          </p>
          {queue.length > 1 && (
            <p className="small subtle" style={{ margin: 0 }}>
              {queue.length === 2
                ? 'Danach folgt eine weitere Notfallmeldung.'
                : `Danach folgen ${queue.length - 1} weitere Notfallmeldungen.`}
            </p>
          )}
        </div>

        <div className="emergency-actions">
          <button className="btn" onClick={() => acknowledge(false)} disabled={busy}>
            Zur Kenntnis genommen
          </button>
          <button className="btn danger" onClick={() => acknowledge(true)} disabled={busy}>
            Auftrag öffnen
          </button>
        </div>
      </div>
    </div>
  );
}
